import React, { useMemo, useRef, useState } from 'react';
import { FormGenericContainer } from '../FormGenericContainer';
import { BubbleAccordion, BubbleButton, BubbleInput } from '../BubbleUI';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { canManageLibraries } from '@go-shared/lib/permissions';
import Fuse from 'fuse.js';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import { BareEventTemplate, EventTemplate } from '@go-shared/types/eventTemplate';
import { forceIdToString, safeIdCompare } from '@go-shared/lib/utils';
import { bare, populate } from '@go-client/lib/api/eventTemplate';
import { User } from '@go-shared/types/user';
import { intersection } from '@go-shared/lib/functional';
import { BubbleAccordionRef } from '../BubbleUI/BubbleAccordion';
import { Brand } from '@go-shared/types/brand';
import { Property } from '@go-shared/types/property';
import { TextHighlighted } from '@go-client/components/Common/TextHighlighted';
import { Plan } from '@go-shared/types/plan';
import { CanvasEventFormMode } from './CanvasEventForm';
import { evaluateAudienceZone } from '@go-shared/lib/audience';
import EventTemplateFormik, {
  EventTemplateFormikMode,
  EventTemplateFormikRef,
  EvtTmplFormikExtState,
} from '../Formik/EventTemplateFormik';

interface SelectEventPageProps {
  user: User;
  property: Property;
  brand: Brand;
  mode: CanvasEventFormMode;
  plan: Plan;
  title?: string;
  selected?: EventTemplate;
  onCreate?: (tmpl: EventTemplate) => void;
  onSelect?: (tmpl: EventTemplate | undefined) => void;
  eventTemplates: EventTemplate[];
  onGoNext?: () => void;
  onClose?: () => void;
}

export default function SelectEventPage(props: SelectEventPageProps) {
  const { user, selected: selected } = props;

  const [formikMode, setFormikMode] = useState<EventTemplateFormikMode>({
    type: 'filter',
    staffTemplates: [],
  });
  const [formikState, setFormikState] = useState<EvtTmplFormikExtState | undefined>(undefined);
  const [typeaheadFilterAll, setTypeaheadFilterAll] = useState(false);

  const formikRef = useRef<EventTemplateFormikRef>(null);
  const accordionRef = useRef<BubbleAccordionRef>(null);

  function handleSelectEvtTemplate(eventTmpl: EventTemplate | undefined) {
    if (props.onSelect) props.onSelect(eventTmpl);
  }

  async function handleCreateEventTemplate(bareTemplate: BareEventTemplate) {
    if (props.onCreate) {
      props.onCreate(populate(bareTemplate, props.brand));
      accordionRef.current?.toggle(1);
    }
  }

  function handleCopySelectedEvtTemplate() {
    accordionRef.current?.toggle(0);
    setFormikMode({
      type: 'create',
      edit: selected && bare(selected),
      staffTemplates: [],
    });
    handleSelectEvtTemplate(undefined);
  }

  const evtTmplItems = useMemo(() => {
    return props.eventTemplates
      .map(e => ({
        id: e._id,
        title: e.title,
        description: e.description,
        _template: e,
      }))
      .filter(e => !e._template.archived);
  }, [props.eventTemplates]);

  const titleFuse = useMemo(() => {
    return new Fuse(evtTmplItems, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['title'],
      useExtendedSearch: true,
    });
  }, [evtTmplItems]);

  const descFuse = useMemo(() => {
    return new Fuse(evtTmplItems, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['description'],
    });
  }, [evtTmplItems]);

  const titleFuseSearchResults = useMemo(() => {
    const title = formikState?.values.title;
    if (!title || !titleFuse) return undefined;

    const criteria = `'${title}`;
    return titleFuse.search(criteria).map(s => s.item);
  }, [titleFuse, formikState?.values.title]);

  const descFuseSearchResults = useMemo(() => {
    const description = formikState?.values.description;
    if (!description || !descFuse) return undefined;

    const criteria = `'${description}`;
    return descFuse.search(criteria).map(s => s.item);
  }, [descFuse, formikState?.values.description]);

  let filteredItems = evtTmplItems;

  if (titleFuseSearchResults) {
    filteredItems = intersection(filteredItems, titleFuseSearchResults, 'id');
  }

  if (descFuseSearchResults) {
    filteredItems = intersection(filteredItems, descFuseSearchResults, 'id');
  }

  filteredItems = filteredItems.filter(wi => {
    let meetsSegmentCheck = true;
    let meetsLevelCheck = true;
    let meetsAttributesCheck = true;
    let meetsAudienceCheck = true;

    if (formikState?.values.segment) {
      meetsSegmentCheck = safeIdCompare(wi._template.segment?._id, formikState?.values.segment);
    }

    if (formikState?.values.levels.length) {
      const len = intersection(
        [...wi._template.levels].map(l => forceIdToString(l._id)),
        formikState?.values.levels
      ).length;
      meetsLevelCheck = typeaheadFilterAll ? len === formikState?.values.levels.length : len > 0;
    }

    if (formikState?.values.attributes.length) {
      const len = intersection(
        [...wi._template.attributes].map(a => forceIdToString(a._id)),
        formikState?.values.attributes
      ).length;
      meetsAttributesCheck = typeaheadFilterAll
        ? len === formikState?.values.attributes.length
        : len > 0;
    }

    if (formikState?.values.audienceZoneFilter) {
      meetsAudienceCheck = evaluateAudienceZone(
        'visible',
        formikState?.values.audienceZoneFilter,
        wi._template.audience,
        undefined
      );
    }

    return meetsSegmentCheck && meetsLevelCheck && meetsAttributesCheck && meetsAudienceCheck;
  });

  return (
    <div className="cef-1">
      <FormGenericContainer
        className="absolute top-0 left-0 w-full h-full flex flex-col"
        bodyClassName="p-0-i flex-grow relative bg-eggshell"
        footerClassName="p-4-i border-t"
        footer={() => (
          <>
            {selected && (
              <div className="text-slate-dark">
                <div>
                  Event Selected:
                  <span className="font-semibold"> {selected.title}</span>
                </div>
              </div>
            )}
            <div className="ml-auto" />
            <BubbleButton
              variant="slate"
              inverse
              onClick={() => setTypeaheadFilterAll(!typeaheadFilterAll)}
              icon="filter"
            >
              Typeahead Filter Mode: {typeaheadFilterAll ? 'All' : 'Any'}
            </BubbleButton>
            <BubbleButton variant="navy" disabled={!props.selected} onClick={props.onGoNext}>
              Next: Enter Event Details
            </BubbleButton>
          </>
        )}
      >
        <div
          className={`absolute left-0 top-0 h-full p-6 ${
            selected ? 'overflow-clip' : 'overflow-y-auto overflow-x-hidden'
          }`}
          style={{ width: 'calc(100% - 400px)' }}
        >
          <div className="flex items-start mb-8">
            <div>
              <h1 className="text-lg text-slate-dark font-semibold">
                {props.title || 'Add Event'}
              </h1>
              <h2 className="text-2xl text-navy">1. Select Event</h2>
            </div>
            <BubbleButton
              onClick={props.onClose}
              className="ml-auto"
              variant="navy"
              inverse
              icon="xmark-large"
              iconSize="xl"
            />
          </div>
          <EventTemplateFormik
            ref={formikRef}
            mode={formikMode}
            actionsClassName="hidden"
            subnavClassName="hidden"
            onStateUpdate={setFormikState}
            onSubmit={handleCreateEventTemplate}
          />
          <div
            className="absolute top-0 left-0 w-full h-full bg-black opacity-25 cursor-pointer transition-transform"
            onClick={() => handleSelectEvtTemplate(undefined)}
            style={{
              transform: `translateX(${selected ? '0' : '100%'})`,
            }}
          />
          <div
            className="absolute left-0 bottom-0 z-10 w-full p-6 overflow-y-auto overflow-x-hidden bg-eggshell transition-transform"
            style={{
              height: '90%',
              transform: `translateX(${selected ? '0' : '100%'})`,
              pointerEvents: selected ? 'auto' : 'none',
            }}
          >
            <div className="flex items-start mb-8">
              <div>
                <h1 className="text-lg text-slate-dark font-semibold">
                  {selected?.title ?? 'Unknown Template'}
                </h1>
                <h2 className="text-2xl text-navy">Event Details</h2>
              </div>
              <div className="ml-auto" />
              {canManageLibraries(user) && (
                <BubbleButton
                  variant="banana"
                  icon="copy"
                  inverse
                  onClick={handleCopySelectedEvtTemplate}
                  tabIndex={-1}
                >
                  Duplicate This Event
                </BubbleButton>
              )}
              <BubbleButton
                onClick={() => handleSelectEvtTemplate(undefined)}
                variant="navy"
                inverse
                icon="xmark-large"
                iconSize="xl"
                tabIndex={-1}
              />
            </div>
            {selected && (
              <EventTemplateFormik
                mode={{ type: 'view', edit: bare(selected), staffTemplates: [] }}
                actionsClassName="hidden"
                subnavClassName="hidden"
              />
            )}
          </div>
        </div>

        <div className="absolute right-0 top-0 h-full bg-white border-l" style={{ width: '400px' }}>
          <BubbleAccordion
            ref={accordionRef}
            variant="navy-light"
            singleMode
            noToggle
            headerClassName="px-4 py-4"
            className="h-full flex flex-col"
            open={[1]}
            hideLocked
            locked={canManageLibraries(user) ? [] : [0]}
            labels={['Create a New Event', 'Use an Existing Event']}
            onOpen={idx => {
              if (idx === 0) {
                handleSelectEvtTemplate(undefined);
                setFormikMode({
                  type: 'create',
                  staffTemplates: [],
                });
              } else {
                setFormikMode({
                  type: 'filter',
                  staffTemplates: [],
                });
              }
            }}
            headerFn={(label, open, locked) => (
              <>
                {locked ? (
                  <>
                    <FontAwesomeIcon icon="lock" size="lg" fixedWidth />
                    <span className="font-semibold ml-2-5">{label}</span>
                  </>
                ) : (
                  <>
                    <BubbleInput
                      checked={open}
                      type="checkbox"
                      appearance="rounded"
                      className="mr-3"
                      inputClassName="bg-white"
                    />
                    <span className="font-semibold">{label}</span>
                  </>
                )}
              </>
            )}
          >
            <div className="p-4">
              <div className="text-smoke italic mb-3">
                Complete the new event details by using the form in the left column. Once finished,
                click the button below to create and select this event.
              </div>
              <BubbleButton
                variant="navy"
                disabled={formikState?.disabled ?? true}
                onClick={formikRef?.current?.submit}
              >
                Create & Select New Event
              </BubbleButton>
            </div>
            <div className="p-4">
              <div className="text-smoke italic mb-3">
                Use the form located in the left column to filter existing events. The list will be
                further filtered as you input information into each field within the form.
              </div>
              <div className="grid grid-cols-1">
                {filteredItems.map(itm => (
                  <div className="relative py-1" key={itm.id}>
                    <BubbleInput
                      type="checkbox"
                      className="mr-2"
                      inputClassName="stretched-link bg-white"
                      checked={safeIdCompare(selected?._id, itm._template._id)}
                      appearance="rounded"
                      onValue={chk => {
                        handleSelectEvtTemplate(chk ? itm._template : undefined);
                      }}
                    />
                    <TextHighlighted text={itm.title} highlight={formikState?.values.title} />
                  </div>
                ))}
              </div>
            </div>
          </BubbleAccordion>
        </div>
      </FormGenericContainer>
    </div>
  );
}
